import GeoJSON from 'ol/format/GeoJSON';
import Overlay from 'ol/Overlay';
import Draw from 'ol/interaction/Draw';
import { unByKey } from 'ol/Observable';
import { makeVectorLayer } from '../layers/index';
import {
    isInsideWorkingArea,
    isInSelectedMuncipality,
    toolTipIsVisible,
    addToolTipBase,
    clearToolTipBase,
    getWorkingAreaRestrictedMsg,
    isInPlanMunicipality,
    getPlanRestrictedMsg,
} from './tooltip';
import { bus } from '../../main';
import store from '@/store';

const geojsonFormat = new GeoJSON();
const mapEventKeys = {};
const polygonToolEventKeys = {};
const planPolygonToolEventKeys = {};
let isDrawingPolygon = false;

export const pointToolResultPopup = new Overlay({
    autoPan: true,
    autoPanAnimation: {
        duration: 100
    }
});

export const setPointToolResultPopupTarget = (element) =>
{
    pointToolResultPopup.setElement(element);
};

// bat point tool
const pointLayer = makeVectorLayer({
    id: 'bat-point-tool-layer',
    type: 'Vector',
    visible: true,
    zIndex: 2,
    vector_params: {
        url: null,
        style: {
            geom: 'Point',
            fill:{ color:'rgba(0,240,240,0.1)' },
            stroke: {color: 'rgb(0,240,240)', width:2},
            radius: 5,
        },
    }
});


const pointTool = new Draw({
    source: pointLayer.getSource(),
    type: 'Point',
});

// bat polygon tool
const polygonLayer = makeVectorLayer({
    id: 'bat-polygon-tool-layer',
    type: 'Vector',
    visible: true,
    zIndex: 2,
    vector_params: {
        url: null,
        style: {
            geom: 'Polygon',
            fill:{ color:'rgba(0,240,240,0.05)' },
            stroke: {color: 'rgb(0,240,240)', width:1},
        }
    }
});

const polygonTool = new Draw({
    source: polygonLayer.getSource(),
    type: 'Polygon',
    freehandCondition: () => false,
    condition: event => isInsideWorkingArea(event.coordinate) && isInSelectedMuncipality(event.coordinate),
});


const planPolygonTool = new Draw({
    source: polygonLayer.getSource(),
    type: 'Polygon',
    freehandCondition: () => false,
    condition: event =>
    {
        // console.log( store.dispatch('plan/isInPlanMunicipality', event.coordinate) );
        return isInSelectedMuncipality(event.coordinate) && isInPlanMunicipality(event.coordinate);
    },
});

export const isDrawingWithPolygonTool = () => isDrawingPolygon;

export const closeBATPointQueryOverlay = function ()
{
    pointToolResultPopup.setPosition(undefined);
    pointLayer.getSource().clear();
};

// has to be called once, once the map is initialized
// adds all layers, overlays & draw interactions to the map
// and also all the event handlers
export const setupBatTools = (map) =>
{
    map.addLayer(pointLayer);
    map.addInteraction(pointTool);
    pointTool.setActive(false);
    map.addOverlay(pointToolResultPopup);
    
    pointTool.on('drawend', function(event)
    {
        const coordinates = event.feature.getGeometry().getCoordinates();
        store.dispatch('tools/performBatPointAnalysis', coordinates)
        .then( ({success}) => success && pointToolResultPopup.setPosition(coordinates) );
        this.setActive(false);
        clearToolTip();
    });
    
    map.addLayer(polygonLayer);
    map.addInteraction(polygonTool);
    polygonTool.setActive(false);

    map.addInteraction(planPolygonTool);
    planPolygonTool.setActive(false);

    window.addEventListener('keydown', event =>
    {
        if (toolIsActive())
        {
            switch (event.key)
            {
            case "Escape":
                pointTool.setActive(false);
                polygonTool.setActive(false);
                planPolygonTool.setActive(false);
                bus.$emit('bat-draw-canceled', true);
                clearToolTip();
                break;
            case 'Shift':
                toolTipIsVisible() ? 
                    clearToolTip({hideOnly: true})
                    :
                    addToolTip(null,null,{showOnly: true});
                break;
            }
        }
    });
};


const toolIsActive = () =>
{
    return pointTool.getActive() || polygonTool.getActive() || planPolygonTool.getActive();
};

const addToolTip = (map, msg, {showOnly=false}={}) =>
{
    if (showOnly)
    {
        addToolTipBase(null, null, {showOnly});
        return;
    }

    const getMessage = coordinate =>
    {
        // displaying info msg as per the conditions is only for bat polygon tool
        if (polygonTool.getActive())
        {
            return getWorkingAreaRestrictedMsg(coordinate, msg);
        }
        else if (planPolygonTool.getActive())
        {
            return getPlanRestrictedMsg(coordinate, msg);
        }
        else
        {
            return msg;
        }
    }
    const eventKey = addToolTipBase(map, getMessage);
    mapEventKeys.hover = eventKey;
};

const clearToolTip  = ({hideOnly=false}={}) =>
{
    clearToolTipBase({hideOnly});
    unByKey(mapEventKeys.hover);
}

export const resetTools = () => {
    pointLayer.getSource().clear();
    polygonLayer.getSource().clear();
    pointTool.setActive(false);
    polygonTool.setActive(false);
    Object.values(polygonToolEventKeys).forEach(({type, listener}) =>
    {
        polygonTool.un(type, listener);
    });
    
    planPolygonTool.setActive(false);
    Object.values(planPolygonToolEventKeys).forEach(({type, listener}) =>
    {
        planPolygonTool.un(type, listener);
    });
    pointToolResultPopup.setPosition(undefined);
    clearToolTip();
};

export const selectPointTool = () => {
    resetTools();
    pointTool.setActive(true);
    addToolTip(pointTool.getMap(), "Click on a location / Press 'Esc' to cancel");
};

export const selectPolygonTool = () => {
    resetTools();
    polygonTool.setActive(true);
    addToolTip(polygonTool.getMap(), "Click to start drawing / Press 'Esc' to cancel");

    polygonToolEventKeys.drawstart =  polygonTool.once('drawstart', () =>
    {
        // set isDrawingPolygon to true when drawing starts
        isDrawingPolygon = true;
        addToolTip(
            polygonTool.getMap(), `
            <ul style="margin-left:1.5em;">
                <li>Press 'Shift' to hide/show this tooltip.</li>
                <li>Click to continue drawing</li>
                <li>Click on the starting vertex to complete polygon</li>
                <li>Press 'Esc' to cancel.</li>
            </ul>`,
        );
    });

    polygonToolEventKeys.drawend = polygonTool.once('drawend', function(event)
    {
        isDrawingPolygon = false;
        const polygon = geojsonFormat.writeGeometry(event.feature.getGeometry());
        store.dispatch('tools/performBatZonalAnalysis', polygon)
        this.setActive(false);
        clearToolTip();
    });
};


export const selectPlanPolygonTool = (mapNavigator, sddmm) => {
    resetTools();
    mapNavigator.selectMunicipalityFromTop(sddmm);
    planPolygonTool.setActive(true);
    addToolTip(planPolygonTool.getMap(), "Click to start drawing / Press 'Esc' to cancel");

    planPolygonToolEventKeys.drawstart =  planPolygonTool.once('drawstart', () =>
    {
        // set isDrawingPolygon to true when drawing starts
        isDrawingPolygon = true;
        addToolTip(
            planPolygonTool.getMap(), `
            <ul style="margin-left:1.5em;">
                <li>Press 'Shift' to hide/show this tooltip.</li>
                <li>Click to continue drawing</li>
                <li>Click on the starting vertex to complete polygon</li>
                <li>Press 'Esc' to cancel.</li>
            </ul>`,
        );
    });

    planPolygonToolEventKeys.drawend = planPolygonTool.once('drawend', function(event)
    {
        isDrawingPolygon = false;
        const polygon = geojsonFormat.writeGeometry(event.feature.getGeometry());
        bus.$emit('bat-cluster-drawend', polygon);
        this.setActive(false);
        clearToolTip();
    });
};
